import React from "react";
import { useTranslation } from "react-i18next";
import { useLanguageSwitch } from "../../../hooks/useLanguageSwitch";
import { useThemeContext } from "../../../contexts/ThemeContext";
import { ThemeStyle, ThemeMode } from "../../../styles/theme";
import {
  SettingsContainer,
  SettingGroup,
  SettingLabel,
  SettingSelect,
} from "./AppearanceSettings.styles";

const GlobeIcon = () => (
  <svg
    width="1.1rem"
    height="1.1rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const PaletteIcon = () => (
  <svg
    width="1.1rem"
    height="1.1rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"></circle>
    <circle cx="17.5" cy="10.5" r=".5" fill="currentColor"></circle>
    <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"></circle>
    <circle cx="6.5" cy="12.5" r=".5" fill="currentColor"></circle>
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"></path>
  </svg>
);

const MoonIcon = () => (
  <svg
    width="1.1rem"
    height="1.1rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
  </svg>
);

export const AppearanceSettings: React.FC = () => {
  const { t, i18n } = useTranslation(["common", "nav"]);
  const { switchLanguage } = useLanguageSwitch();
  const { themeStyle, themeMode, setThemeStyle, setThemeMode } =
    useThemeContext();

  return (
    <SettingsContainer>
      <SettingGroup>
        <SettingLabel>
          <GlobeIcon />
          {t("nav:language", "Language")}
        </SettingLabel>
        <SettingSelect
          value={i18n.language}
          onChange={(e) => switchLanguage(e.target.value)}
        >
          <option value="en">English</option>
          <option value="uk">Українська</option>
          <option value="ru">Русский</option>
        </SettingSelect>
      </SettingGroup>

      <SettingGroup>
        <SettingLabel>
          <PaletteIcon />
          {t("nav:theme_type", "Theme Style")}
        </SettingLabel>
        <SettingSelect
          value={themeStyle}
          onChange={(e) => setThemeStyle(e.target.value as ThemeStyle)}
        >
          <option value="corporate">Corporate</option>
          <option value="modern">Modern</option>
          <option value="synthwave">Synthwave</option>
        </SettingSelect>
      </SettingGroup>

      <SettingGroup>
        <SettingLabel>
          <MoonIcon />
          {t("nav:mode", "Theme Mode")}
        </SettingLabel>
        <SettingSelect
          value={themeMode}
          onChange={(e) => setThemeMode(e.target.value as ThemeMode)}
        >
          <option value="light">Light</option>
          <option value="dark">Dark</option>
        </SettingSelect>
      </SettingGroup>
    </SettingsContainer>
  );
};
